<script setup lang="ts">
import {
  buildUrlValidation,
  getUrlValidationDomains,
  isValidDomain,
  normalizeDomain,
  normalizeUrlValidation,
  type UrlValidation,
  type UrlValidationMode,
} from "@/util/urlField";
import { XCloseIcon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton } from "@gohighlevel/ghl-ui";
import { computed, h, ref, type FunctionalComponent } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  modelValue?: UrlValidation | null;
}>();

const emit = defineEmits<{
  (event: "update:modelValue", value: UrlValidation): void;
}>();

const { t } = useI18n();
const domainDraft = ref("");
const hasInvalidDomain = ref(false);

/**
 * Builds a 1.67px-stroke line icon component from SVG path data.
 *
 * @param paths - SVG path `d` values drawn on a 24px grid.
 * @returns A functional component rendering the icon.
 */
const strokeIcon = (...paths: string[]): FunctionalComponent =>
  function StrokeIcon() {
    return h(
      "svg",
      {
        xmlns: "http://www.w3.org/2000/svg",
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        "stroke-width": "1.67",
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
      },
      paths.map((d) => h("path", { d }))
    );
  };

const GlobeIcon = strokeIcon(
  "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z",
  "M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z"
);
const ShieldCheckIcon = strokeIcon(
  "M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z",
  "m9 12 2 2 4-4"
);
const BlockIcon = strokeIcon(
  "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z",
  "m4.9 4.9 14.2 14.2"
);

const validation = computed(() => normalizeUrlValidation(props.modelValue));
const domains = computed(() => getUrlValidationDomains(validation.value));
const isRestricted = computed(() => validation.value.mode !== "allow_all");
const hasEmptyDomains = computed(() => domains.value.length === 0);
const domainLabel = computed(() =>
  validation.value.mode === "block"
    ? t("common.urlField.domainsBlocked")
    : t("common.urlField.domainsAllowed")
);
const domainPlaceholder = computed(() =>
  hasEmptyDomains.value
    ? t("common.urlField.domainPlaceholder")
    : t("common.urlField.domainPlaceholderAdd")
);

const modes = computed<
  Array<{
    value: UrlValidationMode;
    label: string;
    description: string;
    icon: FunctionalComponent;
  }>
>(() => [
  {
    value: "allow_all",
    label: t("common.urlField.allowAllDomains"),
    description: t("common.urlField.allowAllDomainsDescription"),
    icon: GlobeIcon,
  },
  {
    value: "allow_only",
    label: t("common.urlField.allowOnlyDomains"),
    description: t("common.urlField.allowOnlyDomainsDescription"),
    icon: ShieldCheckIcon,
  },
  {
    value: "block",
    label: t("common.urlField.blockDomains"),
    description: t("common.urlField.blockDomainsDescription"),
    icon: BlockIcon,
  },
]);

/**
 * Updates the pending domain value and clears its validation error.
 *
 * @param event - Input event from the domain draft field.
 */
const updateDomainDraft = (event: Event) => {
  domainDraft.value = (event.target as HTMLInputElement).value;
  hasInvalidDomain.value = false;
};

/**
 * Changes the domain validation mode while preserving applicable domains.
 *
 * @param mode - Selected URL validation mode.
 */
const updateMode = (mode: UrlValidationMode) => {
  domainDraft.value = "";
  hasInvalidDomain.value = false;
  emit(
    "update:modelValue",
    buildUrlValidation(mode, mode === "allow_all" ? [] : domains.value)
  );
};

/**
 * Validates and adds the pending domain to the URL validation configuration.
 */
const addDomain = () => {
  if (!domainDraft.value.trim()) return;

  const domain = normalizeDomain(domainDraft.value);
  if (!isValidDomain(domain)) {
    hasInvalidDomain.value = true;
    return;
  }

  emit(
    "update:modelValue",
    buildUrlValidation(validation.value.mode, [...domains.value, domain])
  );
  domainDraft.value = "";
  hasInvalidDomain.value = false;
};

/**
 * Removes a configured domain by its current list index.
 *
 * @param index - Index of the domain to remove.
 */
const removeDomain = (index: number) => {
  emit(
    "update:modelValue",
    buildUrlValidation(
      validation.value.mode,
      domains.value.filter((_, domainIndex) => domainIndex !== index)
    )
  );
};

/**
 * Handles chip-input keys: Enter/comma add, Backspace removes the last chip.
 *
 * @param event - Keydown event from the domain draft field.
 */
const handleDomainKeydown = (event: KeyboardEvent) => {
  if (event.key === "Enter" || event.key === ",") {
    event.preventDefault();
    addDomain();
    return;
  }

  if (
    event.key === "Backspace" &&
    !domainDraft.value &&
    domains.value.length > 0
  ) {
    removeDomain(domains.value.length - 1);
  }
};

</script>

<template>
  <div class="flex flex-col gap-3">
    <p class="m-0 text-[13px] leading-[18px] text-gray-600">
      {{ t("common.urlField.description") }}
    </p>

    <div
      role="radiogroup"
      :aria-label="t('common.urlField.settingsTitle')"
      class="flex flex-col gap-2"
    >
      <label
        v-for="mode in modes"
        :key="mode.value"
        :data-testid="`url-mode-${mode.value}`"
        class="flex cursor-pointer items-start gap-2.5 rounded-lg border-[1.5px] border-solid p-3"
        :class="
          validation.mode === mode.value
            ? 'border-primary-600 bg-primary-25'
            : 'border-gray-200 bg-white'
        "
      >
        <input
          :id="`url-mode-${mode.value}`"
          class="sr-only"
          type="radio"
          name="url-domain-mode"
          :value="mode.value"
          :checked="validation.mode === mode.value"
          @change="updateMode(mode.value)"
        />
        <span
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
          :class="
            validation.mode === mode.value
              ? 'bg-primary-100 text-primary-600'
              : 'bg-gray-100 text-gray-500'
          "
        >
          <component :is="mode.icon" class="h-[18px] w-[18px]" />
        </span>
        <span class="flex flex-1 flex-col gap-0.5">
          <span class="text-sm font-medium text-gray-900">{{
            mode.label
          }}</span>
          <span class="text-[13px] leading-[18px] text-gray-500">{{
            mode.description
          }}</span>
        </span>
        <span
          class="mt-1.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-solid"
          :class="
            validation.mode === mode.value
              ? 'border-primary-600 bg-primary-600'
              : 'border-gray-300 bg-white'
          "
        >
          <span
            v-if="validation.mode === mode.value"
            class="h-[7px] w-[7px] rounded-full bg-white"
          />
        </span>
      </label>
    </div>

    <div v-if="isRestricted" class="flex flex-col gap-1.5">
      <label for="url-domain-input" class="text-sm font-medium text-gray-700">
        {{ domainLabel }} <span class="text-error-600">*</span>
      </label>

      <label
        data-testid="url-domain-input"
        class="domain-input-box flex min-h-[36px] cursor-text flex-wrap items-center gap-1.5 rounded-lg bg-white px-2 py-[5px] shadow-sm"
        :class="{
          'domain-input-box--error': hasInvalidDomain || hasEmptyDomains,
        }"
      >
        <span
          v-for="(domain, index) in domains"
          :key="domain"
          :data-testid="`url-domain-chip-${index}`"
          class="flex items-center gap-1.5 rounded-md border border-solid border-gray-300 bg-gray-50 px-2 py-0.5 text-sm font-medium text-gray-700"
        >
          {{ domain }}
          <UIButton
            :id="`url-domain-remove-${index}`"
            :quaternary="true"
            text
            :aria-label="`${t('common.remove')} ${domain}`"
            @click.stop="removeDomain(index)"
          >
            <XCloseIcon class="h-3 w-3 text-gray-400" />
          </UIButton>
        </span>

        <input
          id="url-domain-input"
          class="h-6 min-w-[130px] flex-1 border-none bg-transparent p-0 px-0.5 text-sm text-gray-900 outline-none placeholder:text-gray-400"
          :value="domainDraft"
          :placeholder="domainPlaceholder"
          @input="updateDomainDraft"
          @keydown="handleDomainKeydown"
        />
      </label>

      <p
        v-if="hasInvalidDomain"
        data-testid="url-domain-invalid"
        class="m-0 text-[13px] leading-[18px] text-error-600"
      >
        {{ t("common.urlField.invalidDomain") }}
      </p>
      <p
        v-else-if="hasEmptyDomains"
        data-testid="url-domain-required"
        class="m-0 text-[13px] leading-[18px] text-error-600"
      >
        {{ t("common.urlField.domainRequired") }}
      </p>
      <p v-else class="m-0 text-[13px] leading-[18px] text-gray-500">
        {{ t("common.urlField.domainHint") }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Scoped border styles: single-class Tailwind border utilities can be
   overridden by host-app stylesheets when the builder runs embedded. */
.domain-input-box {
  border: 1px solid #d0d5dd;
}

.domain-input-box:focus-within {
  border-color: #2970ff;
}

.domain-input-box--error,
.domain-input-box--error:focus-within {
  border-color: #d92d20;
}
</style>
